import { useEffect, useRef, useState } from "react";
import { apiFetch, apiJson, assetUrl } from "../utils/api";
import { CARD_TEMPLATES, normalizeTemplate, paintHarvestCardAsync } from "../utils/harvestCard";
import SpeechInputButton from "../components/SpeechInputButton";
import WeChatLoginButton from "../components/WeChatLoginButton";
import { formatBeijingDateTime } from "../utils/datetime";

const FEELING_PROMPTS = [
  "今天最触动我的一句话",
  "我对爱情/婚姻的新理解",
  "今天我想到的一个问题",
  "今天我最想记住的一句话",
];

function formatTime(iso) {
  return formatBeijingDateTime(iso, {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function BookClubShareSection({ current }) {
  const userId = localStorage.getItem("user_id");

  const [shares, setShares] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [message, setMessage] = useState("");
  const [aiText, setAiText] = useState("");
  const [regenLeft, setRegenLeft] = useState(3);
  const [cardOn, setCardOn] = useState(false);
  const [cardTpl, setCardTpl] = useState("dawn");
  const [headerPhotoUrl, setHeaderPhotoUrl] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [shareHint, setShareHint] = useState("");
  const canvasRef = useRef(null);
  const photoInputRef = useRef(null);

  const pageSize = 15;
  const loginRedirect = "/book-club";

  const loadShares = async () => {
    if (!current?.id) return;
    setLoading(true);
    try {
      const res = await apiFetch(
        `/api/public/book-club/shares?reading_id=${current.id}&page=${page}&page_size=${pageSize}`
      );
      const data = await res.json();
      if (res.ok) {
        setShares(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [current?.id]);

  useEffect(() => {
    loadShares();
  }, [current?.id, page]);

  useEffect(() => {
    if (!userId) return;
    apiJson(`/api/reading/checkin/today?user_id=${userId}`).then(({ res, data }) => {
      if (!res.ok || !data?.item) return;
      if (data.item.original_text) setContent(data.item.original_text);
      if (data.item.ai_text) setAiText(data.item.ai_text);
      setRegenLeft(data.item.regenerate_left ?? 3);
      if (data.item.card_template) setCardTpl(normalizeTemplate(data.item.card_template));
      if (data.item.card_image_url) setHeaderPhotoUrl(data.item.card_image_url);
    });
  }, [userId]);

  useEffect(() => {
    if (!cardOn || !canvasRef.current || !aiText) return;
    let cancelled = false;
    (async () => {
      const url = headerPhotoUrl ? assetUrl(headerPhotoUrl) : "";
      await paintHarvestCardAsync(canvasRef.current, {
        text: aiText,
        bookTitle: current.book_title,
        template: cardTpl,
        dateLabel: todayStr(),
        headerImageUrl: url || null,
      });
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [cardOn, aiText, cardTpl, headerPhotoUrl, current.book_title]);

  const generateHarvest = async (regenerate = false) => {
    if (!userId) {
      alert("请先登录");
      return;
    }
    const text = content.trim();
    if (text.length < 2) {
      setMessage("写下一两句话就可以，不需要写得很完整。");
      return;
    }
    setPosting(true);
    setMessage("");
    try {
      const { res, data } = await apiJson("/api/reading/checkin/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: Number(userId),
          original_text: text,
          regenerate,
        }),
      });
      if (!res.ok) {
        setMessage(data?.detail || "AI暂时没有回应，请稍后再试。");
        return;
      }
      setAiText(data.item?.ai_text || "");
      setRegenLeft(data.item?.regenerate_left ?? 0);
      setCardOn(false);
    } catch (err) {
      setMessage("AI暂时没有回应，请稍后再试。");
    } finally {
      setPosting(false);
    }
  };

  const openCard = async (tpl = cardTpl, photoUrl = headerPhotoUrl) => {
    setPosting(true);
    setMessage("");
    try {
      const { res, data } = await apiJson("/api/reading/checkin/card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: Number(userId),
          template: tpl,
          header_image_url: photoUrl || "",
        }),
      });
      if (!res.ok) {
        setMessage(data?.detail || "内容已经保存，分享卡片稍后可以重新生成。");
        return;
      }
      setCardTpl(normalizeTemplate(data.item?.card_template || tpl));
      if (data.item?.card_image_url !== undefined) {
        setHeaderPhotoUrl(data.item.card_image_url || "");
      }
      setCardOn(true);
      setMessage("今日晨读已完成。卡片只保存在你这里，不会自动公开。");
    } catch (err) {
      setMessage("内容已经保存，分享卡片稍后可以重新生成。");
    } finally {
      setPosting(false);
    }
  };

  const chooseStyle = (tpl) => {
    setCardTpl(tpl);
    setHeaderPhotoUrl("");
    if (!userId) return;
    apiJson("/api/reading/checkin/card", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: Number(userId),
        template: tpl,
        header_image_url: "",
      }),
    }).catch(() => {});
  };

  const uploadHeaderPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    setPhotoUploading(true);
    setMessage("");
    try {
      const body = new FormData();
      body.append("user_id", String(userId));
      body.append("file", file);
      const res = await apiFetch("/api/reading/checkin/upload-header", {
        method: "POST",
        body,
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "照片上传失败");
        return;
      }
      setHeaderPhotoUrl(data.url || "");
      setCardOn(true);
      await openCard(cardTpl, data.url || "");
      setShareHint("已换成你的照片。分享出去别人更容易注意到你。");
    } catch (err) {
      alert("照片上传失败，请重试");
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const saveCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `今日收获-${todayStr()}.png`;
    a.click();
  };

  const shareCard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const file = new File([blob], "today-harvest.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "我的今日收获" });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
    setShareHint("长按图片保存到手机，然后分享到朋友圈。");
  };

  const publish = async () => {
    if (!userId) {
      alert("请先微信登录后再发布分享");
      return;
    }
    const text = (content.trim() || aiText.trim());
    if (!text) {
      alert("请写下今天的阅读分享");
      return;
    }
    setPosting(true);
    setMessage("");
    try {
      const res = await apiFetch("/api/book-club/shares", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: Number(userId),
          reading_id: current.id,
          content: text,
          share_date: todayStr(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(typeof data.detail === "string" ? data.detail : "发布失败，请稍后重试");
        return;
      }
      setContent("");
      setAiText("");
      setCardOn(false);
      setMessage("分享已发布，感谢你的留言！");
      setPage(1);
      loadShares();
    } catch (err) {
      alert("网络不稳定，请稍后重试");
    } finally {
      setPosting(false);
    }
  };

  const removeOwn = async (shareId) => {
    if (!userId || !window.confirm("删除这条分享？")) return;
    const res = await apiFetch(`/api/book-club/shares/${shareId}?user_id=${userId}`, {
      method: "DELETE",
    });
    if (res.ok) loadShares();
  };

  if (!current) return null;

  const featured = shares.filter((s) => s.is_featured);
  const others = shares.filter((s) => !s.is_featured);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden">
      <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-6 py-3 text-white text-sm font-medium">
        每日精选分享 · 《{current.book_title}》
      </div>

      <div className="p-6 md:p-8 space-y-6">
        <p className="text-sm text-gray-500 leading-relaxed">
          读完今天的内容，写下你的心得、收获或疑问。管理员可将优质分享设为「精选」展示在前列。
        </p>

        {userId ? (
          <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4 space-y-3">
            <p className="text-xs text-amber-700">今日分享 · {todayStr()}</p>
            <h3 className="text-lg font-bold text-stone-900">今天读完，你有什么感受？</h3>
            <p className="text-xs text-stone-500">
              不需要写文章，一两句话就够了。可直接发布到读书会；也可以先让 AI 整理成今日收获。
              {typeof window !== "undefined" && /MicroMessenger/i.test(navigator.userAgent)
                ? " 微信里请直接打字留言（语音识别在微信内不可用）。"
                : " 电脑/Chrome 也可点麦克风语音留言。"}
            </p>
            <div className="relative">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value.slice(0, 200))}
                placeholder="写下今天读书后的感受……"
                rows={4}
                className="w-full border border-amber-100 rounded-xl px-3 py-3 pr-14 text-base bg-white resize-none touch-manipulation"
              />
              <div className="absolute right-2 bottom-2">
                <SpeechInputButton
                  disabled={posting}
                  maxLength={200}
                  getPrefix={() => content}
                  onResult={(text) => {
                    if (text) setContent(String(text).slice(0, 200));
                  }}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {FEELING_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setContent((prev) => (prev.trim() ? prev : `${p}：`))}
                  className="px-2.5 py-1 rounded-full bg-white text-[11px] text-amber-900 border border-amber-100"
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-400">{content.length}/200</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={publish}
                disabled={posting || content.trim().length < 2}
                className="w-full min-h-[44px] px-4 py-2.5 rounded-full bg-amber-600 text-white text-sm font-semibold disabled:opacity-50 touch-manipulation"
              >
                {posting ? "发布中..." : "发布到读书会"}
              </button>
              <button
                type="button"
                onClick={() => generateHarvest(false)}
                disabled={posting || content.trim().length < 2}
                className="w-full min-h-[44px] px-4 py-2.5 rounded-full bg-stone-800 text-white text-sm font-semibold disabled:opacity-50 touch-manipulation"
              >
                {posting ? "整理中..." : "AI生成我的今日收获"}
              </button>
            </div>
            {aiText && (
              <div className="rounded-xl bg-white border border-stone-100 p-3 space-y-3">
                <p className="text-xs text-stone-400">我的今日收获</p>
                <p className="text-base leading-relaxed text-stone-800">{aiText}</p>
                {current.book_title && (
                  <p className="text-xs text-stone-500">今日共读 《{current.book_title}》</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={posting}
                    onClick={() => openCard(cardTpl)}
                    className="min-h-[40px] px-4 py-2 rounded-full bg-amber-700 text-white text-sm touch-manipulation"
                  >
                    生成分享卡片
                  </button>
                  <button
                    type="button"
                    disabled={posting || regenLeft <= 0}
                    onClick={() => generateHarvest(true)}
                    className="min-h-[40px] px-4 py-2 rounded-full border text-sm disabled:opacity-40 touch-manipulation"
                  >
                    重新生成{regenLeft > 0 ? `（${regenLeft}）` : "已用完"}
                  </button>
                </div>
              </div>
            )}
            {cardOn && (
              <div className="space-y-2">
                <div className="flex gap-2 overflow-x-auto pb-1 items-center">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadHeaderPhoto}
                  />
                  <button
                    type="button"
                    disabled={photoUploading || posting}
                    onClick={() => photoInputRef.current?.click()}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border touch-manipulation ${
                      headerPhotoUrl
                        ? "bg-rose-600 text-white border-rose-600"
                        : "bg-white text-rose-700 border-rose-200"
                    }`}
                  >
                    {photoUploading ? "上传中…" : headerPhotoUrl ? "换我的照片" : "用我的照片"}
                  </button>
                  {CARD_TEMPLATES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => chooseStyle(t.key)}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
                        !headerPhotoUrl && cardTpl === t.key
                          ? "bg-stone-800 text-white border-stone-800"
                          : "bg-white text-stone-700"
                      }`}
                    >
                      <span
                        className="w-3.5 h-3.5 rounded-full border border-white/70"
                        style={{ background: t.swatch }}
                      />
                      {t.label}
                    </button>
                  ))}
                </div>
                <canvas ref={canvasRef} className="w-full h-auto rounded-xl shadow" />
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={saveCard} className="py-2.5 rounded-full bg-stone-800 text-white text-sm">
                    保存图片
                  </button>
                  <button type="button" onClick={shareCard} className="py-2.5 rounded-full border text-sm">
                    分享
                  </button>
                </div>
                <p className="text-[11px] text-stone-500 leading-relaxed">
                  {shareHint ||
                    "可点「用我的照片」把头部换成自己的照片，更容易吸引关注；也可继续用五种风景。"}
                </p>
              </div>
            )}
            {message && <p className="text-xs text-amber-900">{message}</p>}
          </div>
        ) : (
          <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-5 space-y-3">
            <p className="text-sm font-semibold text-amber-900 text-center">
              微信登录后，每日可分享收获与疑问
            </p>
            <p className="text-xs text-amber-800/80 text-center leading-relaxed">
              晨读打卡留言需登录。推荐微信一键登录；也可用手机号/邮箱账号。
            </p>
            <WeChatLoginButton redirect={loginRedirect} label="微信登录后打卡分享" />
          </div>
        )}

        {loading ? (
          <p className="text-center text-gray-400 py-6">加载分享中...</p>
        ) : shares.length === 0 ? (
          <p className="text-center text-gray-400 py-6">还没有分享，来做第一个留言的人吧</p>
        ) : (
          <div className="space-y-6">
            {featured.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-amber-600">✦ 精选分享</h3>
                {featured.map((item) => (
                  <ShareCard
                    key={item.id}
                    item={item}
                    userId={userId}
                    onDelete={removeOwn}
                    featured
                  />
                ))}
              </div>
            )}
            {others.length > 0 && (
              <div className="space-y-3">
                {featured.length > 0 && (
                  <h3 className="text-sm font-bold text-gray-700">会员分享</h3>
                )}
                {others.map((item) => (
                  <ShareCard key={item.id} item={item} userId={userId} onDelete={removeOwn} />
                ))}
              </div>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-3 pt-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
            >
              上一页
            </button>
            <span className="text-sm text-gray-500 self-center">
              {page} / {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function ShareCard({ item, userId, onDelete, featured = false }) {
  return (
    <article
      className={`rounded-xl p-4 text-sm ${
        featured ? "bg-amber-50 border border-amber-200" : "bg-gray-50 border border-gray-100"
      }`}
    >
      <div className="flex items-start gap-3">
        <img
          src={assetUrl(item.author_avatar) || "https://picsum.photos/40"}
          alt=""
          className="w-9 h-9 rounded-full object-cover shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-medium text-gray-800">{item.author_name}</span>
            {item.share_date && (
              <span className="text-xs text-gray-400">阅读日 {item.share_date}</span>
            )}
            <span className="text-xs text-gray-400">{formatTime(item.created_at)}</span>
            {featured && (
              <span className="text-xs px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full">精选</span>
            )}
          </div>
          <p className="text-gray-700 whitespace-pre-line leading-relaxed">{item.content}</p>
        </div>
        {userId && Number(userId) === item.user_id && (
          <button
            type="button"
            onClick={() => onDelete(item.id)}
            className="text-xs text-gray-400 hover:text-red-500 shrink-0"
          >
            删除
          </button>
        )}
      </div>
    </article>
  );
}
